import { getApiBaseUrl } from '@/lib/api';

/**
 * Prompt Optimizer API Service
 * Provides AI-powered prompt optimization for AI Agent and AI Batch nodes
 */

export interface PromptOptimizerRequest {
  prompt: string;
  promptType: 'system' | 'user';
  nodeType: 'ai-agent' | 'ai-batch';
  connectedTools?: string[];
}

export interface PromptOptimizerResponse {
  success: boolean;
  optimizedPrompt?: string;
  error?: string;
  tokensUsed: number;
}

export interface PromptOptimizerOptions {
  getAccessToken: () => Promise<string>;
  subscriptionId?: string;
}

/**
 * Optimize a prompt using AI
 * @param request - The optimization request with prompt and context
 * @param options - Auth options
 * @returns Promise with the optimization result
 */
export async function optimizePrompt(
  request: PromptOptimizerRequest,
  options: PromptOptimizerOptions
): Promise<PromptOptimizerResponse> {
  try {
    const token = await options.getAccessToken();
    const baseUrl = getApiBaseUrl();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    if (options.subscriptionId) {
      headers['X-Subscription-Id'] = options.subscriptionId;
    }

    const response = await fetch(`${baseUrl}/api/promptoptimizer/optimize`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = 'Failed to optimize prompt';
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error || errorJson.message || errorMessage;
      } catch {
        if (errorText) errorMessage = errorText;
      }
      return { success: false, error: errorMessage, tokensUsed: 0 };
    }

    return await response.json();
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
    return { success: false, error: errorMessage, tokensUsed: 0 };
  }
}
